class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    // Always re-query animations so newly added/changed CSS animations are picked up
    // and so each open is a fresh playback rather than a resume from a finished state.
    this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => {
        // Restart from the beginning so the animation plays on every open,
        // not just the first one.
        animation.cancel();
        animation.play();
      });
    }
    // On close, intentionally do NOT cancel running animations. The CSS rule
    // `.header__submenu { transition: opacity ..., transform ... }` will take
    // over once the [open] attribute is gone and fade the panel out smoothly.
    // Cancelling here would snap the panel to opacity:0 instantly and produce
    // the blink the user reported.
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');

    // Open on hover (pointer devices only — leaves click/keyboard behavior
    // intact for touch screens and assistive tech).
    this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
    // Keep the panel open briefly after the cursor leaves so transient gaps
    // (e.g. moving from summary to the absolutely-positioned submenu) don't
    // cause a close/reopen flicker.
    this.closeDelay = 50;
    this.closeTimer = null;

    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);

    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);
  }

  onPointerEnter(event) {
    if (!this.hoverMedia.matches || event.pointerType !== 'mouse') return;
    clearTimeout(this.closeTimer);
    this.open();
  }

  onPointerLeave(event) {
    if (!this.hoverMedia.matches || event.pointerType !== 'mouse') return;
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), this.closeDelay);
  }

  open() {
    if (!this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.setAttribute('open', '');
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
      this.onToggle();
    }
    // Restart the open animation every time, even if [open] was already set
    this.restartOpenAnimation();
  }

  close() {
    super.close();
    this.onToggle();
  }

  restartOpenAnimation() {
    // Re-trigger the CSS animation on the panel so it plays on every open,
    // not just the first time the [open] attribute is added.
    const panel = this.content;
    if (!panel) return;
    // Force a reflow + restart by removing and re-adding the animation property.
    // This works even when the [open] attribute didn't change (e.g. quick hover swap).
    panel.style.animation = 'none';
    // Force the browser to compute the "no animation" state before reapplying.
    // Reading offsetWidth triggers a synchronous reflow.
    void panel.offsetWidth;
    panel.style.animation = '';

    // Also nudge any Web Animations API animations to replay from the start.
    panel.getAnimations().forEach((animation) => {
      animation.cancel();
      animation.play();
    });
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

/* ============================================
   Nested header submenu flyout animation
   Inline-style driven, bypasses CSS cascade conflicts.
   Targets <details> elements nested INSIDE the standard dropdown's
   .header__submenu panel and animates their child <ul> flyouts.
   ============================================ */
(function () {
  const FLYOUT_DISTANCE = '1rem';
  const FLYOUT_DURATION = 250; // ms

  function styleFlyoutHidden(panel) {
    panel.style.transition = 'none';
    panel.style.opacity = '0';
    panel.style.transform = 'translateX(-' + FLYOUT_DISTANCE + ')';
    panel.style.visibility = 'hidden';
    panel.style.pointerEvents = 'none';
  }

  function styleFlyoutBase(panel) {
    panel.style.display = 'block';
    panel.style.position = 'absolute';
    panel.style.top = '0';
    panel.style.left = '100%';
    panel.style.margin = '0';
    panel.style.minWidth = '20rem';
    panel.style.zIndex = '5';
    panel.style.animation = 'none';
  }

  function repositionPanel(panel, details) {
    // Anchor the panel to the right edge of the <details>, vertically aligned
    // with the summary so there's no gap the cursor has to jump.
    const summary = details.querySelector('summary');
    if (!summary) return;
    const sRect = summary.getBoundingClientRect();
    const liRect = details.closest('li').getBoundingClientRect();
    panel.style.left = liRect.width + 'px';
    panel.style.top = (sRect.top - liRect.top) + 'px';
  }

  function showFlyout(panel) {
    // Idempotent — if the panel is already shown (or in the process of being
    // shown), don't snap it back to opacity:0 and restart the fade. Restarting
    // is what produced the "blink then reappear" the user reported when
    // crossing back over an already-open row.
    if (panel.dataset.flyoutState === 'open') return;
    panel.dataset.flyoutState = 'open';

    panel.style.visibility = 'visible';
    panel.style.pointerEvents = 'auto';
    panel.style.transition =
      'opacity ' + FLYOUT_DURATION + 'ms ease, transform ' + FLYOUT_DURATION + 'ms ease';
    // Force a reflow so the transition starts from whatever the current value
    // is (e.g. mid-fade-out if we're reopening before hide finished).
    void panel.offsetWidth;
    panel.style.opacity = '1';
    panel.style.transform = 'translateX(0)';
  }

  function hideFlyout(panel) {
    if (panel.dataset.flyoutState === 'closed') return;
    panel.dataset.flyoutState = 'closed';

    panel.style.transition =
      'opacity ' + FLYOUT_DURATION + 'ms ease, transform ' + FLYOUT_DURATION + 'ms ease';
    panel.style.opacity = '0';
    panel.style.transform = 'translateX(-' + FLYOUT_DISTANCE + ')';
    panel.style.pointerEvents = 'none';
    setTimeout(() => {
      // Only commit visibility:hidden if the panel is still meant to be
      // closed — a re-open during the fade-out may have flipped state back.
      if (panel.dataset.flyoutState === 'closed') panel.style.visibility = 'hidden';
    }, FLYOUT_DURATION);
  }
// Snap every OTHER flyout shut immediately, with no fade.
  //
  // Without this, moving from one parent row to another leaves the previous
  // panel fading out (250ms close delay + 250ms fade) while the new one is
  // already fading in — so for roughly half a second two panels of different
  // heights sit stacked on top of each other in the same spot.
  function closeOtherFlyouts(current) {
    document.querySelectorAll('.header__submenu [data-flyout-ready]').forEach(function (other) {
      if (other === current || other.dataset.flyoutState !== 'open') return;
      other.dataset.flyoutState = 'closed';
      other.style.transition = 'none';
      other.style.opacity = '0';
      other.style.transform = 'translateX(-' + FLYOUT_DISTANCE + ')';
      other.style.visibility = 'hidden';
      other.style.pointerEvents = 'none';
    });
  }
  function initFlyout(details) {
    const panel = details.querySelector(':scope > .header__submenu, :scope > ul');
    if (!panel) return;
    if (panel.dataset.flyoutReady) return;
    panel.dataset.flyoutReady = '1';

    styleFlyoutBase(panel);
    styleFlyoutHidden(panel);
    panel.dataset.flyoutState = 'closed';

    details.setAttribute('open', '');
    const li = details.closest('li');
    if (li) li.style.position = 'relative';
    repositionPanel(panel, details);

    let leaveTimer = null;
     const open = () => {
      clearTimeout(leaveTimer);
      closeOtherFlyouts(panel);
      repositionPanel(panel, details);
      showFlyout(panel);
    };
    const scheduleClose = () => {
      clearTimeout(leaveTimer);
      leaveTimer = setTimeout(() => hideFlyout(panel), 250);
    };

    // The flyout panel is a DOM descendant of `li`, so li.mouseleave fires
    // ONLY when the cursor leaves the li AND the panel (mouseleave doesn't
    // fire when moving between an element and its descendants — even when the
    // descendant is absolutely positioned outside the parent's visual box).
    // That's exactly the behavior we want, so we listen only on the li.
    //
    // The previous code also attached mouseenter/mouseleave on the panel,
    // which caused two bugs:
    //   1. Moving from the panel BACK to the parent row fired panel.mouseleave
    //      (scheduling a close) but did NOT re-fire li.mouseenter (because the
    //      cursor never conceptually left the li), so nothing cancelled the
    //      close timer and the flyout would disappear.
    //   2. Moving between panel and row could repeatedly fire panel.mouseenter,
    //      each call resetting the show animation and producing a blink.
    const target = li || details;
    target.addEventListener('mouseenter', open);
    target.addEventListener('mouseleave', scheduleClose);

    target.addEventListener('focusin', open);
    target.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!target.contains(document.activeElement)) scheduleClose();
      }, 0);
    });
  }

  function initAllFlyouts() {
    document.querySelectorAll('.header__submenu details').forEach(initFlyout);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllFlyouts);
  } else {
    initAllFlyouts();
  }
})();
